import moment from 'moment';
import { TriggerType } from 'src/models/reports/ReportVersions';

// Pure reporting-period helpers shared by the ReportsService hooks and the tpTaxSection components.
// Kept in the models layer (not under the component tree) so the service does not depend on a
// component-level module.

// Fiscal quarter-end-close months (1-based): March, June, September, December — the same set the MEC
// Soft Close button gates on (see components/mec/status/StatusView.tsx). Duplicated here (not imported)
// to avoid a reporting-module dependency on the MEC status view; both derive from the same fiscal rule.
export const QUARTER_END_CLOSE_MONTHS = [3, 6, 9, 12];

// The reporting periods returned by getReportingPeriods() are display-formatted (e.g. "MAR-26").
const REPORTING_PERIOD_FORMAT = 'MMM-YY';

// The ICTPReportingOrchestrator API expects the period in yyyyMM format (e.g. "202604"), while the
// reporting-period selector uses the MMM-YY display format (e.g. "APR-26"). Convert at the API boundary.
const ORCHESTRATOR_PERIOD_FORMAT = 'YYYYMM';

/**
 * Returns true when a reporting period string (e.g. "MAR-26") falls in a fiscal quarter-end-close
 * month. Mirrors isQuarterEndPeriod in StatusView, adapted to the MMM-YY reporting-period format.
 */
export function isQuarterEndReportingPeriod(period: string): boolean {
    if (!period) {
        return false;
    }
    const parsed = moment(period, REPORTING_PERIOD_FORMAT, true);
    if (!parsed.isValid()) {
        return false;
    }
    return QUARTER_END_CLOSE_MONTHS.includes(parsed.month() + 1);
}

/**
 * Converts a display reporting period ("MMM-YY", e.g. "APR-26") to the yyyyMM format the orchestrator
 * API requires ("202604"). Returns an empty string for empty / unparseable input.
 */
export function toOrchestratorPeriod(period: string): string {
    if (!period) {
        return '';
    }
    const parsed = moment(period, REPORTING_PERIOD_FORMAT, true);
    return parsed.isValid() ? parsed.format(ORCHESTRATOR_PERIOD_FORMAT) : '';
}

// The accounting-day values the report pages key report data on ({period}#{accountingDay}).
export const ACCOUNTING_DAY_7 = '7';
export const ACCOUNTING_DAY_11 = '11';
export const ACCOUNTING_DAY_ON_DEMAND = 'ONDEMAND';

// The only accounting-day values the dashboard hands to a report page.
const VALID_ACCOUNTING_DAYS = new Set([ACCOUNTING_DAY_7, ACCOUNTING_DAY_11, ACCOUNTING_DAY_ON_DEMAND]);

/**
 * True when a period (MMM-YY) + accounting day pair is a well-formed dashboard selection. Used to reject
 * malformed values arriving via the URL query (e.g. a hand-edited or stale deep link) before they reach
 * the report fetch, so a garbage link is treated like a missing selection (redirect to the dashboard)
 * rather than firing a doomed backend request that yields a cryptic transport-level error banner. Period
 * validity reuses the strict MMM-YY parse (toOrchestratorPeriod returns '' for anything unparseable); the
 * accounting day must be one of the three known values.
 */
export function isValidReportSelection(period: string | null, accountingDay: string | null): boolean {
    if (!period || !accountingDay) {
        return false;
    }
    return toOrchestratorPeriod(period) !== '' && VALID_ACCOUNTING_DAYS.has(accountingDay);
}

/**
 * Derives the accounting day for a selected report version from its trigger type and reporting period,
 * mirroring the orchestrator's own derivation (TriggerMessageParser): ON_DEMAND runs use the ONDEMAND
 * sentinel; a SOFT_CLOSE (only ever raised in a quarter-end-close month) is Day 7; a scheduled HANDOFF is
 * Day 11 in a quarter-end-close period (Mar/Jun/Sep/Dec) and Day 7 otherwise. The version DTO has no
 * structured accountingDay, so it is derived here rather than parsed from the display label.
 */
export function deriveAccountingDay(triggerType: string, period: string): string {
    if (triggerType === TriggerType.ON_DEMAND) {
        return ACCOUNTING_DAY_ON_DEMAND;
    }
    if (triggerType === TriggerType.SOFT_CLOSE) {
        return ACCOUNTING_DAY_7;
    }
    // HANDOFF (scheduled): Day 11 at quarter-end close, Day 7 otherwise.
    return isQuarterEndReportingPeriod(period) ? ACCOUNTING_DAY_11 : ACCOUNTING_DAY_7;
}
